/**
 * portalClient — axios instance for the admin portal.
 *
 * Separate from the main `api_instance` because a dead session ends
 * differently here (back to /portal/login, not the analytics sign-in). The TOKENS are
 * shared, though: /admin/auth/login issues the same access + refresh pair as
 * /auth/login, kept in the tokenStorage cookies (see portalAuthStore for why
 * there is only one copy). On a 401 this refreshes through the shared
 * POST /auth/refresh and retries once; only a failed refresh ends the session.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { refreshSession } from "./refreshSession";
import { tokenStorage } from "./tokenStorage";
import { API_ENDPOINTS } from "../constants/api";
import { usePortalAuthStore } from "../store/portalAuthStore";

const portalClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
    ClientDb: process.env.REACT_APP_CLIENT_DB || "",
  },
  timeout: 30_000,
});

// Attach the bearer token when a session exists. Read from the cookie on
// every request so a refresh done by either client is picked up immediately.
portalClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

/** Session is over: clear the portal flag and the shared tokens, then re-login. */
function endSession() {
  usePortalAuthStore.getState().clearSession();
  tokenStorage.clearTokens();
  // Lets authStore drop its in-memory copy too (it listens for this event —
  // importing the store here directly would be circular).
  window.dispatchEvent(new CustomEvent("auth:unauthorized"));
  window.location.assign("/portal/login");
}

function extractApiMessage(error: AxiosError) {
  const data = error.response?.data as Record<string, unknown> | undefined;
  if (data?.detail) {
    if (typeof data.detail === "string") {
      error.message = data.detail;
    } else if (Array.isArray(data.detail)) {
      const msgs = data.detail
        .map((d: { msg?: string }) => d.msg)
        .filter(Boolean);
      if (msgs.length) error.message = msgs.join("; ");
    }
  }
}

portalClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // A 401 from the login call itself is a wrong password, not a dead
    // session — let the form show it. Matched by URL rather than "was a
    // bearer attached?": with shared cookies, a leftover analytics-app token
    // can ride along on the login request too.
    const isLogin = original?.url?.includes(API_ENDPOINTS.PORTAL.LOGIN);

    if (error.response?.status === 401 && original && !isLogin) {
      // Already retried once with a fresh token and still 401 — give up.
      if (original._retry) {
        endSession();
        return Promise.reject(error);
      }
      original._retry = true;

      // A refresh token can outlive the access cookie (7 days vs 2 hours),
      // so this also covers requests that went out with no bearer at all.
      if (tokenStorage.getRefreshToken()) {
        try {
          const newAccessToken = await refreshSession();
          original.headers.Authorization = `Bearer ${newAccessToken}`;
          return portalClient(original);
        } catch (refreshError) {
          endSession();
          return Promise.reject(refreshError);
        }
      }

      endSession();
    }

    extractApiMessage(error);
    return Promise.reject(error);
  },
);

export default portalClient;
